import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DescriptionList,
  ErrorState,
  PageHeader,
  RiskBadge,
  SelectField,
  SkeletonRows,
  StatCard,
  TextField,
  type BadgeTone,
  type Column,
} from '@csa/ui';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';

/**
 * The entitlement snapshot, for a user access review.
 *
 * The report is the deliverable, so the page is built for the way a review
 * is actually conducted: a reviewer works through rows, and the ones that
 * need a decision have to be identifiable without reading all of them. So
 * the findings -- segregation-of-duties conflicts, active break-glass,
 * dormant accounts, accounts with no roles -- are counted at the top and
 * filterable in one click.
 *
 * There is no "approve" button. Sign-off is not recorded here, deliberately:
 * a tick box in the tool under review, controlled by the same
 * administrators, is not evidence of independent review. The evidence is the
 * audit event recording that this report was generated, plus the exported
 * pack the reviewer annotates outside the system.
 */

interface ReviewRow {
  readonly userId: string;
  readonly upn: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly entraOid: string;
  readonly enabled: boolean;
  readonly disabledReason: string | null;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly roles: readonly {
    readonly roleId: string;
    readonly name: string;
    readonly source: 'direct' | 'entra-group' | 'breakglass';
    readonly grantedAt: string;
    readonly grantedByUpn: string | null;
    readonly expiresAt: string | null;
    readonly justification: string;
    readonly viaGroups: readonly string[];
  }[];
  readonly permissions: readonly string[];
  readonly targets: readonly {
    readonly targetId: string;
    readonly targetKey: string;
    readonly environment: string;
    readonly permissions: readonly string[];
  }[];
  readonly highestRisk: 'none' | 'low' | 'medium' | 'high' | 'critical';
  readonly separationIssues: readonly {
    readonly severity: 'block' | 'warn';
    readonly permissions: readonly [string, string];
    readonly rationale: string;
  }[];
  readonly hasActiveBreakGlass: boolean;
  readonly dormant: boolean;
}

interface Report {
  readonly generatedAt: string;
  readonly dormantAfterDays: number;
  readonly userCount: number;
  readonly rows: readonly ReviewRow[];
  readonly summary: {
    readonly withHighRisk: number;
    readonly withSeparationIssues: number;
    readonly withActiveBreakGlass: number;
    readonly dormant: number;
    readonly disabled: number;
    readonly withoutRoles: number;
  };
}

type Lens = 'all' | 'sod' | 'breakglass' | 'dormant' | 'no-roles' | 'high-risk' | 'disabled';

const SOURCE_TONES: Record<ReviewRow['roles'][number]['source'], BadgeTone> = {
  direct: 'neutral',
  'entra-group': 'info',
  breakglass: 'danger',
};

export function AccessReviewPage(): ReactElement {
  const tz = useTimeZone();
  const [lens, setLens] = useState<Lens>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReviewRow | null>(null);

  const report = useQuery({
    queryKey: ['access-review'],
    /**
     * Not refetched on focus or on an interval. Generating the report is an
     * audited event -- it is the evidence that a review took place -- and a
     * page that regenerated it every time somebody switched back to the tab
     * would fill the ledger with reviews nobody conducted.
     */
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: () => api.get<Report>('/api/admin/access-review'),
  });

  const rows = useMemo(() => {
    const all = report.data?.rows ?? [];
    const term = search.trim().toLowerCase();

    const lensed = all.filter((row) => {
      switch (lens) {
        case 'sod':
          return row.separationIssues.length > 0;
        case 'breakglass':
          return row.hasActiveBreakGlass;
        case 'dormant':
          return row.dormant;
        case 'no-roles':
          return row.roles.length === 0;
        case 'high-risk':
          return row.highestRisk === 'high' || row.highestRisk === 'critical';
        case 'disabled':
          return !row.enabled;
        default:
          return true;
      }
    });

    if (!term) return lensed;
    return lensed.filter(
      (row) =>
        row.upn.toLowerCase().includes(term) ||
        row.displayName.toLowerCase().includes(term) ||
        row.roles.some((role) => role.name.toLowerCase().includes(term)),
    );
  }, [report.data, lens, search]);

  const columns = useMemo<Column<ReviewRow>[]>(
    () => [
      {
        key: 'upn',
        header: 'User',
        render: (row) => (
          <div className="flex flex-col">
            <span className={row.enabled ? '' : 'line-through opacity-60'}>{row.displayName}</span>
            <span className="text-xs text-subtle">{row.upn}</span>
          </div>
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (row) =>
          row.roles.length === 0 ? (
            <Badge tone="warning">none</Badge>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.roles.map((role) => (
                <Badge key={`${role.roleId}-${role.source}`} tone={SOURCE_TONES[role.source]}>
                  {role.name}
                </Badge>
              ))}
            </div>
          ),
      },
      {
        key: 'highestRisk',
        header: 'Risk',
        render: (row) =>
          row.highestRisk === 'none' ? (
            <span className="text-subtle">—</span>
          ) : (
            <RiskBadge risk={row.highestRisk} />
          ),
      },
      {
        key: 'targets',
        header: 'Targets',
        render: (row) => (
          <span className="csa-numeric text-xs">
            {row.targets.length === 0 ? '—' : row.targets.length}
          </span>
        ),
      },
      {
        key: 'findings',
        header: 'Findings',
        render: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.separationIssues.some((i) => i.severity === 'block') ? (
              <Badge tone="danger">SoD conflict</Badge>
            ) : row.separationIssues.length > 0 ? (
              <Badge tone="warning">privilege concentration</Badge>
            ) : null}
            {row.hasActiveBreakGlass ? <Badge tone="danger">break-glass</Badge> : null}
            {row.dormant ? <Badge tone="warning">dormant</Badge> : null}
            {!row.enabled ? <Badge tone="neutral">disabled</Badge> : null}
          </div>
        ),
      },
      {
        key: 'lastSeenAt',
        header: `Last seen (${tz.timeZone})`,
        render: (row) => (
          <span className="whitespace-nowrap text-xs" title={tz.titleFor(row.lastSeenAt)}>
            {tz.formatBare(row.lastSeenAt).value}
          </span>
        ),
      },
    ],
    [tz],
  );

  if (report.isPending) return <SkeletonRows rows={10} />;

  if (report.error) {
    return (
      <ErrorState
        message={
          report.error instanceof ApiError
            ? report.error.message
            : 'Could not generate the entitlement snapshot'
        }
        {...(report.error instanceof ApiError ? { code: report.error.code } : {})}
        action={<Button onClick={() => void report.refetch()}>Try again</Button>}
      />
    );
  }

  const data = report.data!;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title="Entitlement snapshot"
        description={`Computed live at ${tz.format(data.generatedAt)}. Accounts not seen for ${data.dormantAfterDays} days are flagged as dormant.`}
        actions={
          <Button
            variant="secondary"
            leadingIcon={<ArrowDownTrayIcon className="size-4" aria-hidden />}
            onClick={() => downloadCsv(data)}
          >
            Download CSV
          </Button>
        }
      />

      {data.summary.withSeparationIssues > 0 ? (
        <Alert
          tone="danger"
          title="Segregation-of-duties conflicts"
          icon={<ExclamationTriangleIcon className="size-5" aria-hidden />}
          actions={
            <Button size="sm" variant="secondary" onClick={() => setLens('sod')}>
              Show them
            </Button>
          }
        >
          {data.summary.withSeparationIssues} account
          {data.summary.withSeparationIssues === 1 ? '' : 's'} hold a combination of permissions
          that lets one person be both maker and checker, or concentrates privilege in a way that
          needs justifying. These arise from the combination of roles a person holds, so no single
          role definition is at fault.
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Accounts" value={data.userCount} onActivate={() => setLens('all')} />
        <StatCard
          label="SoD findings"
          value={data.summary.withSeparationIssues}
          tone={data.summary.withSeparationIssues ? 'danger' : 'default'}
          onActivate={() => setLens('sod')}
        />
        <StatCard
          label="Break-glass"
          value={data.summary.withActiveBreakGlass}
          tone={data.summary.withActiveBreakGlass ? 'warning' : 'default'}
          onActivate={() => setLens('breakglass')}
        />
        <StatCard
          label="High risk"
          value={data.summary.withHighRisk}
          onActivate={() => setLens('high-risk')}
        />
        <StatCard
          label="Dormant"
          value={data.summary.dormant}
          tone={data.summary.dormant ? 'warning' : 'default'}
          onActivate={() => setLens('dormant')}
        />
        <StatCard
          label="No roles"
          value={data.summary.withoutRoles}
          hint="Provisioned but never set up"
          onActivate={() => setLens('no-roles')}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          label="Show"
          containerClassName="w-56"
          value={lens}
          options={[
            { value: 'all', label: 'All accounts' },
            { value: 'sod', label: 'Segregation-of-duties findings' },
            { value: 'breakglass', label: 'Active break-glass' },
            { value: 'high-risk', label: 'High or critical risk' },
            { value: 'dormant', label: 'Dormant' },
            { value: 'no-roles', label: 'No roles' },
            { value: 'disabled', label: 'Disabled accounts' },
          ]}
          onChange={(event) => setLens(event.target.value as Lens)}
        />

        <TextField
          label="Find"
          containerClassName="min-w-64 flex-1"
          placeholder="Name, address or role"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <p className="pb-2 text-xs text-subtle">
          {rows.length.toLocaleString()} of {data.userCount.toLocaleString()} shown
        </p>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.userId}
        onRowClick={setSelected}
        caption="Entitlements by account"
        stickyHeader
      />

      {selected ? <UserDetail row={selected} /> : null}
    </div>
  );
}

function UserDetail({ row }: { row: ReviewRow }): ReactElement {
  const tz = useTimeZone();

  return (
    <Card className="flex flex-col gap-4 p-3">
      <DescriptionList
        items={[
          { label: 'Name', value: row.displayName },
          { label: 'Sign-in address', value: row.upn },
          { label: 'Email', value: row.email ?? '—' },
          {
            label: 'Entra object ID',
            // The immutable join key. Shown because it is what an
            // administrator uses to find the same person in Entra, where the
            // UPN may since have changed.
            value: <code className="font-mono text-xs">{row.entraOid}</code>,
          },
          { label: 'Status', value: row.enabled ? 'Enabled' : `Disabled — ${row.disabledReason}` },
          {
            label: 'First seen',
            value: <span title={tz.titleFor(row.firstSeenAt)}>{tz.format(row.firstSeenAt)}</span>,
          },
          {
            label: 'Last seen',
            value: <span title={tz.titleFor(row.lastSeenAt)}>{tz.format(row.lastSeenAt)}</span>,
          },
        ]}
      />

      {row.separationIssues.length > 0 ? (
        <section>
          <h3 className="mb-1.5 text-xs font-medium text-subtle">Segregation of duties</h3>
          <ul className="flex flex-col gap-2">
            {row.separationIssues.map((issue) => (
              <li key={issue.permissions.join('+')}>
                <Alert tone={issue.severity === 'block' ? 'danger' : 'warning'}>
                  <code className="font-mono text-xs">{issue.permissions[0]}</code>
                  {' + '}
                  <code className="font-mono text-xs">{issue.permissions[1]}</code>
                  <p className="mt-1">{issue.rationale}</p>
                </Alert>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h3 className="mb-1.5 text-xs font-medium text-subtle">Roles and how they were assigned</h3>
        {row.roles.length === 0 ? (
          <p className="text-sm text-muted">
            None. This account can do nothing, which is still worth closing out — either it was
            never set up, or its access was removed without the account being disabled.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm">
            {row.roles.map((role) => (
              <li
                key={`${role.roleId}-${role.source}`}
                className="rounded-md border border-line p-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{role.name}</span>
                  <Badge tone={SOURCE_TONES[role.source]}>{role.source}</Badge>
                  {role.expiresAt ? (
                    <Badge tone="warning">expires {tz.format(role.expiresAt)}</Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-muted">
                  Granted {tz.format(role.grantedAt)}
                  {role.grantedByUpn ? ` by ${role.grantedByUpn}` : ''}
                  {role.viaGroups.length > 0 ? ` via ${role.viaGroups.join(', ')}` : ''}
                </p>
                {role.justification ? (
                  <p className="mt-1 text-xs italic text-muted">“{role.justification}”</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 text-xs font-medium text-subtle">Targets reachable</h3>
        {row.targets.length === 0 ? (
          <p className="text-sm text-muted">None.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {row.targets.map((target) => (
              <li key={target.targetId} className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-xs">{target.targetKey}</code>
                <Badge tone={target.environment === 'prod' ? 'danger' : 'neutral'}>
                  {target.environment}
                </Badge>
                <span className="text-xs text-muted">{target.permissions.join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 text-xs font-medium text-subtle">
          Every permission held ({row.permissions.length})
        </h3>
        <div className="flex flex-wrap gap-1">
          {row.permissions.map((permission) => (
            <code key={permission} className="rounded bg-sunken px-1.5 py-0.5 font-mono text-xs">
              {permission}
            </code>
          ))}
        </div>
      </section>
    </Card>
  );
}

/**
 * A flat CSV for the reviewer's own working copy.
 *
 * One row per account with the findings as columns, because a reviewer works
 * in a spreadsheet and annotates as they go. Deliberately *not* the evidence
 * artefact -- that is the signed pack. This is a convenience, and calling it
 * anything else would invite somebody to submit it as evidence.
 *
 * Leading `=`, `+`, `-` and `@` are neutralised for the same reason the
 * result export does it: a display name is user-controlled, and a spreadsheet
 * treats those as formulae.
 */
function downloadCsv(report: Report): void {
  const header = [
    'upn',
    'display_name',
    'enabled',
    'highest_risk',
    'roles',
    'role_sources',
    'targets',
    'permission_count',
    'sod_findings',
    'active_break_glass',
    'dormant',
    'last_seen_utc',
  ];

  const lines = [header.join(',')];

  for (const row of report.rows) {
    lines.push(
      [
        row.upn,
        row.displayName,
        String(row.enabled),
        row.highestRisk,
        row.roles.map((r) => r.name).join('; '),
        row.roles.map((r) => r.source).join('; '),
        row.targets.map((t) => `${t.targetKey}(${t.environment})`).join('; '),
        String(row.permissions.length),
        row.separationIssues.map((i) => `${i.severity}:${i.permissions.join('+')}`).join('; '),
        String(row.hasActiveBreakGlass),
        String(row.dormant),
        row.lastSeenAt,
      ]
        .map(csvCell)
        .join(','),
    );
  }

  const blob = new Blob([`\ufeff${lines.join('\r\n')}\r\n`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `entitlement-snapshot-${report.generatedAt.slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  const neutralised = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(neutralised) ? `"${neutralised.replaceAll('"', '""')}"` : neutralised;
}
